export interface DesktopBackendStatus {
  running: boolean
  pid?: number | null
}

export interface DesktopRuntimeStatus {
  state: string
  last_error?: string | null
  ports: {
    postgres: number
    redis: number
    api: number
  }
  services: Record<string, {
    configured: boolean
    running: boolean
    healthy: boolean
    pid: number | null
    return_code: number | null
  }>
  runtime_file: string
  timestamp: number
}

export interface DesktopSidecarRuntimeStatus {
  state: string
  last_error?: string | null
  ports: {
    postgres: number
    redis: number
  }
  services: Record<string, {
    configured: boolean
    running: boolean
    healthy: boolean
    pid: number | null
    error?: string | null
  }>
  timestamp: number
}

export interface DesktopDiagnosticsExportResult {
  file_path: string
  timestamp: number
}

export interface DesktopBridgeError {
  code: string
  message: string
  details?: Record<string, unknown> | null
}

export interface DesktopErrorMetrics {
  total_errors: number
  by_code: Record<string, number>
}

type TauriInvokeFn = (command: string, args?: Record<string, unknown>) => Promise<unknown>
const desktopBridgeMode = process.env.NEXT_PUBLIC_DESKTOP_BRIDGE ?? 'tauri'

declare global {
  interface Window {
    __TAURI_INTERNALS__?: {
      invoke?: TauriInvokeFn
    }
    __TAURI__?: {
      core?: {
        invoke?: TauriInvokeFn
      }
      tauri?: {
        invoke?: TauriInvokeFn
      }
    }
  }
}

function getTauriInvoke(): TauriInvokeFn | null {
  if (desktopBridgeMode !== 'tauri') {
    return null
  }
  if (typeof window === 'undefined') {
    return null
  }

  const internalInvoke = window.__TAURI_INTERNALS__?.invoke
  if (typeof internalInvoke === 'function') {
    return internalInvoke.bind(window.__TAURI_INTERNALS__)
  }

  const coreInvoke = window.__TAURI__?.core?.invoke
  if (typeof coreInvoke === 'function') {
    return coreInvoke.bind(window.__TAURI__.core)
  }

  const tauriInvoke = window.__TAURI__?.tauri?.invoke
  return typeof tauriInvoke === 'function'
    ? tauriInvoke.bind(window.__TAURI__.tauri)
    : null
}

export function isDesktopBridgeAvailable(): boolean {
  return getTauriInvoke() !== null
}

export async function desktopCall<T>(method: string, params: Record<string, unknown> = {}): Promise<T> {
  const invoke = getTauriInvoke()
  if (!invoke) {
    throw new Error('Tauri desktop bridge is not available')
  }

  return await invoke('desktop_call', {
    method,
    params,
  }) as T
}

export async function getDesktopBackendStatus(): Promise<DesktopBackendStatus> {
  const invoke = getTauriInvoke()
  if (!invoke) {
    return { running: false, pid: null }
  }
  return await invoke('desktop_backend_status') as DesktopBackendStatus
}

export async function restartDesktopBackend(): Promise<DesktopBackendStatus> {
  const invoke = getTauriInvoke()
  if (!invoke) {
    throw new Error('Tauri desktop bridge is not available')
  }
  return await invoke('desktop_backend_restart') as DesktopBackendStatus
}

export async function getDesktopRuntimeStatus(): Promise<DesktopRuntimeStatus> {
  return await desktopCall<DesktopRuntimeStatus>('system.runtime.status')
}

export async function startDesktopRuntime(startMiddleware: boolean = true): Promise<DesktopRuntimeStatus> {
  return await desktopCall<DesktopRuntimeStatus>('system.runtime.start', {
    start_middleware: startMiddleware,
  })
}

export async function stopDesktopRuntime(): Promise<DesktopRuntimeStatus> {
  return await desktopCall<DesktopRuntimeStatus>('system.runtime.stop')
}

export async function getDesktopSidecarStatus(): Promise<DesktopSidecarRuntimeStatus> {
  const invoke = getTauriInvoke()
  if (!invoke) {
    return {
      state: 'unavailable',
      last_error: 'Tauri desktop bridge is not available',
      ports: { postgres: 0, redis: 0 },
      services: {},
      timestamp: Date.now(),
    }
  }
  return await invoke('desktop_sidecar_status') as DesktopSidecarRuntimeStatus
}

export async function startDesktopSidecars(): Promise<DesktopSidecarRuntimeStatus> {
  const invoke = getTauriInvoke()
  if (!invoke) {
    throw new Error('Tauri desktop bridge is not available')
  }
  return await invoke('desktop_sidecar_start') as DesktopSidecarRuntimeStatus
}

export async function stopDesktopSidecars(): Promise<DesktopSidecarRuntimeStatus> {
  const invoke = getTauriInvoke()
  if (!invoke) {
    throw new Error('Tauri desktop bridge is not available')
  }
  return await invoke('desktop_sidecar_stop') as DesktopSidecarRuntimeStatus
}

export async function exportDesktopDiagnostics(): Promise<DesktopDiagnosticsExportResult> {
  const invoke = getTauriInvoke()
  if (!invoke) {
    throw new Error('Tauri desktop bridge is not available')
  }
  return await invoke('desktop_export_diagnostics') as DesktopDiagnosticsExportResult
}

export function parseDesktopBridgeError(error: unknown): DesktopBridgeError {
  const fallback: DesktopBridgeError = {
    code: 'UNKNOWN_DESKTOP_ERROR',
    message: error instanceof Error ? error.message : String(error),
    details: null,
  }

  const rawMessage = error instanceof Error ? error.message : String(error)
  try {
    const parsed = JSON.parse(rawMessage) as Partial<DesktopBridgeError>
    if (typeof parsed.code === 'string' && typeof parsed.message === 'string') {
      return {
        code: parsed.code,
        message: parsed.message,
        details: (parsed.details as Record<string, unknown> | null | undefined) ?? null,
      }
    }
  } catch {
    // Ignore parsing errors and return fallback
  }
  return fallback
}

// ── Browser types & commands ───────────────────────────────────────────────

export interface BrowserSessionInfo {
  session_id: string
  env_id: string
  fingerprint_id: string
  proxy_id: string
  pid: number
  status: string
  started_at: number
}

export async function startBrowserSession(
  envId: string,
  fingerprintId: string,
  proxyId: string,
): Promise<BrowserSessionInfo> {
  const invoke = getTauriInvoke()
  if (!invoke) {
    throw new Error('Tauri desktop bridge is not available')
  }
  return await invoke('desktop_browser_start', {
    env_id: envId,
    fingerprint_id: fingerprintId,
    proxy_id: proxyId,
  }) as BrowserSessionInfo
}

export async function stopBrowserSession(sessionId: string): Promise<BrowserSessionInfo> {
  const invoke = getTauriInvoke()
  if (!invoke) {
    throw new Error('Tauri desktop bridge is not available')
  }
  return await invoke('desktop_browser_stop', {
    session_id: sessionId,
  }) as BrowserSessionInfo
}

export async function getBrowserStatus(sessionId: string): Promise<BrowserSessionInfo | null> {
  const invoke = getTauriInvoke()
  if (!invoke) {
    throw new Error('Tauri desktop bridge is not available')
  }
  return await invoke('desktop_browser_status', {
    session_id: sessionId,
  }) as BrowserSessionInfo | null
}

export async function listBrowserSessions(): Promise<BrowserSessionInfo[]> {
  const invoke = getTauriInvoke()
  if (!invoke) {
    throw new Error('Tauri desktop bridge is not available')
  }
  return await invoke('desktop_browser_list') as BrowserSessionInfo[]
}

export async function systemOpen(path: string): Promise<void> {
  const invoke = getTauriInvoke()
  if (!invoke) {
    throw new Error('Tauri desktop bridge is not available')
  }
  await invoke('desktop_system_open', { path })
}

export async function getDesktopErrorMetrics(): Promise<DesktopErrorMetrics> {
  const invoke = getTauriInvoke()
  if (!invoke) {
    return {
      total_errors: 0,
      by_code: {},
    }
  }
  return await invoke('desktop_error_metrics') as DesktopErrorMetrics
}
